/**
 * Task #2015 — Zod boundary per `PUT /api/admin/ai-tools/service-provider` e
 * per la response `GET /api/admin/ai-routing/available-providers`.
 *
 * Provider accettati:
 *   - canonici: `openai | claude | google | none`;
 *   - custom: stringa `custom:<uuid>` che identifica un endpoint
 *     `custom_llm_endpoints.id` tenant-scoped.
 *
 * Errori 400 strutturati: `{ code, message_it, hint?, suggested_model? }`.
 * Il FE può tradurre i `code` in azioni concrete (es. aprire la card del
 * provider o suggerire un fallback).
 */
import { z } from 'zod';

export const CANONICAL_PROVIDERS = ['openai', 'claude', 'google', 'none'] as const;
export type CanonicalProvider = (typeof CANONICAL_PROVIDERS)[number];

const CUSTOM_PROVIDER_RE = /^custom:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const ProviderRefSchema = z
  .string()
  .min(1)
  .refine(
    (v) => (CANONICAL_PROVIDERS as readonly string[]).includes(v) || CUSTOM_PROVIDER_RE.test(v),
    {
      message:
        "provider deve essere uno tra openai|claude|google|none oppure 'custom:<uuid>'",
    },
  );

export type ProviderRef = z.infer<typeof ProviderRefSchema>;

/**
 * Provider `custom` (agenti: `llm_provider`; endpoint risolti:
 * `llmProvider`) = endpoint Custom AI del tenant. Unico confronto col literal:
 * backend e frontend non ramificano direttamente su `'custom'`.
 */
export function isCustomLlmProvider(provider: string | null | undefined): provider is 'custom' {
  return provider === 'custom';
}

export function isCustomProviderRef(v: string): boolean {
  return CUSTOM_PROVIDER_RE.test(v);
}

export function parseCustomProviderRef(v: string): string | null {
  return CUSTOM_PROVIDER_RE.test(v) ? v.slice('custom:'.length) : null;
}

export function buildCustomProviderRef(endpointId: string): string {
  return `custom:${endpointId}`;
}

export const UpdateServiceProviderInputSchema = z.object({
  service_key: z.string().trim().min(1).max(64),
  provider: ProviderRefSchema,
  expected_revision: z.number().int().nonnegative().optional(),
});

export type UpdateServiceProviderInput = z.infer<typeof UpdateServiceProviderInputSchema>;

// ----------------------------------------------------------------------------
// Errori strutturati 400
// ----------------------------------------------------------------------------

export const SERVICE_PROVIDER_ERROR_CODES = [
  'SERVICE_KEY_NOT_ALLOWED',
  'PROVIDER_INVALID_FORMAT',
  'PROVIDER_UNKNOWN',
  'PROVIDER_NOT_CONFIGURED',
  'PROVIDER_NOT_SUPPORTED_FOR_CAPABILITY',
  'CUSTOM_ENDPOINT_NOT_FOUND',
  'CUSTOM_ENDPOINT_DISABLED',
  'CUSTOM_ENDPOINT_DEGRADED',
  'CUSTOM_ENDPOINT_MISSING_CAPABILITY',
  'NO_SUITABLE_MODEL',
] as const;
export type ServiceProviderErrorCode = (typeof SERVICE_PROVIDER_ERROR_CODES)[number];

export interface ServiceProviderError {
  code: ServiceProviderErrorCode;
  message_it: string;
  hint?: string;
  suggested_model?: { provider: ProviderRef; model_id: string } | null;
}

// ----------------------------------------------------------------------------
// Response `GET /admin/ai-routing/available-providers`
// ----------------------------------------------------------------------------

export const AvailableProviderEntrySchema = z.object({
  id: ProviderRefSchema,
  label: z.string(),
  kind: z.enum(['canonical', 'custom']),
  /** True se il provider supporta la capability richiesta dal service_key. */
  capability_supported: z.boolean(),
  /**
   * Task #2103 — `is_valid` indica se il provider è "live" pronto al routing:
   *   - canonical: `ai_providers.is_valid = true` per (tenant_id, provider_type);
   *   - custom: endpoint con `health_status !== 'down'` e `include_in_routing = true`.
   * La UI usa questo flag per il badge `live` accanto al provider.
   */
  is_valid: z.boolean(),
  /** Modello suggerito (top-1 recommender) per `service_key`. */
  suggested_model: z
    .object({ model_id: z.string(), display_name: z.string().nullable() })
    .nullable(),
  /** Endpoint custom: stato corrente per badge UI (`ok|degraded|down|unknown`). */
  health_status: z.string().optional(),
  /** Endpoint custom: motivo eventuale disabilitazione select item. */
  disabled_reason: z.string().nullable(),
});

export type AvailableProviderEntry = z.infer<typeof AvailableProviderEntrySchema>;

export const AvailableProvidersResponseSchema = z.object({
  service_key: z.string(),
  capability: z.string(),
  providers: z.array(AvailableProviderEntrySchema),
  current: ProviderRefSchema.optional(),
});

export type AvailableProvidersResponse = z.infer<typeof AvailableProvidersResponseSchema>;

// ----------------------------------------------------------------------------
// Rerank mode status — proiezione read-only di GET /ai-tools/rerank-mode.
// La modalità ('disabled' | '<provider>' | 'hybrid:<a>+<b>') è derivata dal
// routing dei due leg (rag_rerank / rag_rerank_secondary), mai persistita.
// ----------------------------------------------------------------------------

export const RERANK_PROVIDER_ID_VALUES = ['openai', 'claude', 'google'] as const;
export type RerankProviderIdValue = (typeof RERANK_PROVIDER_ID_VALUES)[number];

export const RERANK_DOWNGRADE_REASON_CODES = ['PRIMARY_UNAVAILABLE', 'OPENAI_UNAVAILABLE'] as const;
export type RerankDowngradeReasonCode = (typeof RERANK_DOWNGRADE_REASON_CODES)[number];

export const RerankModeStatusSchema = z.object({
  requested: z.string(),
  effective: z.string(),
  fallback_reason: z.string().nullable(),
  openai_available: z.boolean(),
  mode_requested: z.string(),
  mode_effective: z.string(),
  downgrade_reason_code: z.enum(RERANK_DOWNGRADE_REASON_CODES).nullable(),
  downgrade_reason_message_it: z.string().nullable(),
  primary_provider: z.enum(RERANK_PROVIDER_ID_VALUES).nullable(),
  secondary_provider: z.enum(RERANK_PROVIDER_ID_VALUES).nullable(),
  cross_check_enabled: z.boolean(),
});
export type RerankModeStatus = z.infer<typeof RerankModeStatusSchema>;

// ----------------------------------------------------------------------------
// Task #2016 — Conteggio chunk multimodali (Synthesis MM auto-suggest)
// ----------------------------------------------------------------------------

export const MultimodalChunksCountSchema = z.object({
  image_chunks: z.number().int().nonnegative(),
  audio_chunks: z.number().int().nonnegative(),
  video_chunks: z.number().int().nonnegative(),
  total_multimodal: z.number().int().nonnegative(),
});
export type MultimodalChunksCount = z.infer<typeof MultimodalChunksCountSchema>;

/**
 * Risposta di GET /ai-tools/provider-status: stato dei provider canonici del
 * tenant e provider effettivo delle funzioni AI mostrate nel Routing AI.
 */
const AiProviderStatusEntrySchema = z.object({
  active: z.boolean(),
  configured: z.boolean().optional(),
  connection_valid: z.boolean().optional(),
  use_for_embeddings: z.boolean().optional(),
});
export const AiProviderStatusResponseSchema = z.object({
  openai: z.object({ active: z.boolean() }).optional(),
  claude: AiProviderStatusEntrySchema.optional(),
  google: AiProviderStatusEntrySchema.optional(),
  services: z.record(z.string()).optional(),
});
export type AiProviderStatusResponse = z.infer<typeof AiProviderStatusResponseSchema>;
